import { useEffect } from 'react'
import store from 'app/redux/store/store'
import { IOrcamento } from 'app/types/IOrcamento'
import { ItemOrcamentoEmAndamento, OrcamentoEmAndamento, limparOrcamentoEmAndamentoAction } from 'app/redux/orcamentoEmAndamentoSlice'
import { sobrescreverInformacoesAction } from 'app/redux/orcamentoSlice'
import { localizarOrcamentoEmAndamentoOrfaoDeOutraAba, removerOrcamentoEmAndamentoOrfao } from './localizarOrcamentoEmAndamentoOrfaoDeOutraAba'
import { executarComTravaExclusiva } from 'app/utils/travaEntreAbas'
import { PREFIXO_CHAVE_CESTA_LOCALSTORAGE, lerCestaDeChaveLocalStorage } from './useRecuperarCestaProdutosAnterior'
import { IProduto } from 'app/types/produto/IProduto'
import { selecionarProdutosAction } from 'app/redux/produtosSelecionadosSlice'
import { atualizarOrcamento } from 'app/use-cases/orcamento/atualizarOrcamento'
import microuniversoApi from 'app/api/microuniversoApi'
import globalMU from 'globalMU'

declare var window: any
declare var localStorage: any

// 491123 — mesmo princípio de useRecuperarCestaProdutosAnterior.ts: flag de módulo, não redux,
// para rodar só uma vez por carregamento real de página (ver comentário completo naquele
// arquivo). _Menu.cshtml, ao confirmar a restauração pós-login de um orçamento JÁ GRAVADO, seta
// 'restaurandoOrcamentoEmAndamento' no sessionStorage antes de navegar — evita pedir uma SEGUNDA
// confirmação aqui (o usuário já confirmou "restaurar" uma vez, na tela de login).
let jaVerificouOrcamentoEmAndamentoNestaSessao = false

const CHAVE_SESSION_RESTAURANDO_AUTOMATICAMENTE = 'restaurandoOrcamentoEmAndamento'

const TIPO_ORCAMENTO_POR_DESCRICAO: { [descricao: string]: IOrcamento["TipoOrcamento"] } = {
    "Venda": 0,
    "Outras Saídas": 1,
    "Transferências": 2,
}

// 482572 — mesmos 3 tipos persistidos em app/redux/store/store.ts (TIPOS_ORCAMENTO_PERSISTIVEIS) —
// cenário 2 (não gravado) agora oferece recuperação para os 3, não só Venda: a cadeia de restauração
// no servidor (OrcamentoService.RestaurarOrcamentoNaoGravado -> RestaurarDadosPendentesOrcamentoNaoGravado
// no PRG) já é genérica por tipo (OrcamentoVenda/OrcamentoOutraSaida/OrcamentoTransferencia herdam o
// mesmo método da classe base) — não havia limitação real de backend, só escopo reduzido da ficha
// original.
const TIPOS_ORCAMENTO_COM_RECUPERACAO_NAO_GRAVADO: IOrcamento["TipoOrcamento"][] = [0, 1, 2]

const DESCRICAO_TIPO_ORCAMENTO: { [tipo: number]: string } = {
    0: "Venda",
    1: "Outras Saídas",
    2: "Transferência",
}

// 491123 — Numero > 0 é o teste de "já gravado" usado em todo o resto do sistema (não a faixa
// OID<=7 de #98 do APRENDIZADOS — essa é exclusiva de OIDs de entidade como Filial/Cliente/
// Categoria, nunca de Numero/nNumPed; ver `nNumPed <= 0` em OrcamentoVenda.prg e `Numero > 0` em
// Orcamento.cs/orcamentoController.tsx/Totalizador.tsx). Numero == 7 é um sentinela DIFERENTE e
// deliberado (placeholder de pedido importado de e-commerce ainda não linkado a um NumPed real,
// ver OrcamentoVenda.prg/Orcamento.cs: `Numero > 0 && Numero != 7`) — já é um pedido GRAVADO, não
// um orçamento novo, mas precisa do mesmo tratamento de exceção usado no resto do código.
function foiGravado(numero: number | null | undefined) {
    return numero != null && numero > 0 && numero != 7
}

// 482572 — o PRG (orcamentobase.prg::_RestaurarDadosPendentesClassesFilhasAposItens) atribui
// TurnoEntrega direto em cTurnoEntrega sem nenhuma conversão — precisa chegar já como o PRIMEIRO
// CARACTERE maiúsculo da descrição do turno ('M'/'T'/'N'/'I', enum TurnosEntrega em Enums.cs).
// `orcamento.TurnoEntrega`/`item.TurnoEntrega` guardam o CÓDIGO NUMÉRICO do enum (Manhã=0/Tarde=1/
// Noite=2/Indiferente=3), não a letra — usa o texto já pronto de `DescricaoTurnoEntrega` (mesmo
// campo que Orcamento.cs/ItemOrcamento.cs calculam via GetItemDescription do enum) em vez de
// duplicar aqui o mapeamento código→descrição.
//
// DESCRICAO_POR_CODIGO_TURNO_ENTREGA é só fallback: snapshot pode ter sido persistido em
// localStorage ANTES de DescricaoTurnoEntrega entrar no snapshot (sobrevive até QUATRO_DIAS_EM_MS,
// ver store.ts), e nesse caso só o código numérico está disponível.
const DESCRICAO_POR_CODIGO_TURNO_ENTREGA: { [codigo: number]: string } = {
    0: "Manhã",
    1: "Tarde",
    2: "Noite",
    3: "Indiferente",
}

function normalizarTurnoEntregaParaRestauracao(turnoEntrega: unknown, descricaoTurnoEntrega: unknown): string | undefined {
    if (typeof descricaoTurnoEntrega === "string" && descricaoTurnoEntrega.length > 0) {
        return descricaoTurnoEntrega.charAt(0).toUpperCase()
    }

    if (turnoEntrega == null || turnoEntrega === "") return undefined

    const descricao = DESCRICAO_POR_CODIGO_TURNO_ENTREGA[Number(turnoEntrega)]
    return descricao ? descricao.charAt(0).toUpperCase() : undefined
}

// 482572 — cenário "orçamento NOVO ainda não gravado" (Numero === 0): flag de módulo por
// IdentificadorOrcamento, mesmo princípio de useRecuperarCestaProdutosAnterior.ts (roda uma vez por
// orçamento novo; remontar dentro do MESMO orçamento — Itens/Cliente/Entrega... — não repete a
// pergunta; entrar num orçamento DIFERENTE gera IdentificadorOrcamento novo e libera de novo).
let algumaVerificacaoNaoGravadoJaFoiFeita = false
let identificadorOrcamentoUltimaVerificacaoNaoGravado: string | null = null

function jaVerificouEsteOrcamentoNaoGravado(identificadorOrcamento: string | undefined | null): boolean {
    return algumaVerificacaoNaoGravadoJaFoiFeita && identificadorOrcamentoUltimaVerificacaoNaoGravado === (identificadorOrcamento ?? null)
}

function marcarOrcamentoNaoGravadoComoVerificado(identificadorOrcamento: string | undefined | null) {
    algumaVerificacaoNaoGravadoJaFoiFeita = true
    identificadorOrcamentoUltimaVerificacaoNaoGravado = identificadorOrcamento ?? null
}

// 482572 — CodigoCorTinta vem "0" para item que não tem cor (é o default da coluna, não um código
// de cor válido) — nesses casos o CorTinta não é enviado, para o item não cair no fluxo de tinta do
// servidor com uma cor inexistente.
const CODIGO_COR_TINTA_NAO_INFORMADA = "0"

// Só o `Codigo` da cor é lido pelo servidor (Orcamento.cs::AdicionarItem/ProdutoAdicionado e
// OrcamentoRulesFenixWrapper.AdicionarItemTinta) — os demais campos de CorProdutoContract não
// participam da inclusão do item, então não precisam ser persistidos no snapshot.
function obterCorTintaParaRestauracao(item: ItemOrcamentoEmAndamento) {
    const codigoCorTinta = (item.CodigoCorTinta ?? "").trim()
    if (codigoCorTinta === "" || codigoCorTinta === CODIGO_COR_TINTA_NAO_INFORMADA) return undefined

    return { Codigo: codigoCorTinta }
}

// 482572 — monta o payload mínimo de CriarItemOrcamentoContract.cs a partir do snapshot (que só
// guarda um subconjunto de IItemOrcamento, sem os dados ricos de um resultado de busca de produto —
// ver ItemOrcamentoEmAndamento em orcamentoEmAndamentoSlice.ts). Campos client-only de IItemOrcamento
// que TAMBÉM existem no contrato de criação (tipo de entrega, filial, faturamento, datas,
// observação) são deliberadamente OMITIDOS aqui e reaplicados depois via
// RestaurarDadosPendentesOrcamentoNaoGravado (ver restaurarOrcamentoNaoGravadoNoServidor abaixo)
// — mesma divisão de responsabilidade já usada no cenário 1 (gravado). Campos sem contrapartida no
// snapshot (cor de tinta selecionada, disponibilidade, encomenda) ficam com o padrão do servidor —
// mesma limitação que o cenário 1 já aceita.
//
// 482572 — TINTA: o snapshot guarda só a linha base do produto tinta (os corantes são filtrados em
// store.ts::ehComponenteSubordinadoDeTinta e regerados pelo servidor), e o `Codigo` dessa linha é o
// do COMPONENTE base, não o do produto tinta que o usuário escolheu. Para recriar o item é o par
// produto tinta (CodigoProdutoTinta/CODPROTINTA) + cor (CorTinta.Codigo) que o servidor espera:
// Orcamento.cs::AdicionarItem só entra em Rules.AdicionarItemTinta quando CorTinta != null, e o
// wrapper faz `oItens.Adicionar(produto.Codigo, produto.CorTinta.Codigo)` — mandar o código do
// componente base ali recriaria o item errado (ou nem seria aceito: "Código da tinta informado não
// corresponde a um produto final"). O snapshot em si (`dadosOrcamentoEmAndamento`, enviado à parte)
// continua com o `Codigo` do componente, que é por onde o PRG casa os itens ao reaplicar os campos
// pendentes (orcamentobase.prg::_RestaurarDadosPendentes → `loItem.oProduto.ObterIdProduto()`).
function converterItemParaProdutoMinimo(item: ItemOrcamentoEmAndamento) {
    const codigoProdutoTinta = (item.CodigoProdutoTinta ?? "").trim()
    const ehItemDeTinta = codigoProdutoTinta !== ""

    return {
        Codigo: ehItemDeTinta ? codigoProdutoTinta : item.Codigo,
        CorTinta: ehItemDeTinta ? obterCorTintaParaRestauracao(item) : undefined,
        Quantidade: item.Quantidade,
        Ambiente: item.Ambiente,
        Carga: item.Carga,
        Preco: item.PrecoProduto,
        CodigoFilialRetirada: item.CodigoFilialRetirada,
        CodigoFilialTransferencia: item.CodigoFilialTransferencia,
    }
}

// 482572 — cenário 2 (orçamento NOVO ainda não gravado): adiciona os itens do snapshot ao
// orçamento recém-criado e, na mesma ida ao servidor, reaplica os campos pendentes (tipo de
// entrega, filial de retirada/transferência, datas, etc.) via
// orcamentobase.prg::RestaurarDadosPendentesOrcamentoNaoGravado (ver
// OrcamentoService.RestaurarOrcamentoNaoGravado) — substitui o mecanismo antigo, que adicionava os
// itens via adicionarItens e depois reaplicava os campos extras só no Redux local (nunca chegava a
// ir para o servidor). `dadosOrcamentoEmAndamento` leva o snapshot INTEIRO (capa + itens), mesmo
// formato JSON (`{ Orcamento: <snapshot> }`) e mesmos dados já usados por _Menu.cshtml para o
// cenário GRAVADO — antes só ia `{ Itens: itens }`, perdendo os campos de capa (TurnoEntrega,
// ManterReserva, descontos, frete etc. — ver OrcamentoEmAndamento) que o PRG também espera em
// `loDados._orcamento` (ver _RestaurarDadosPendentesClassesFilhasAposItens em orcamentobase.prg).
//
// Retorna se a restauração deu certo — o chamador só recupera a cesta da mesma aba (ver
// recuperarCestaDaMesmaAbaAutomaticamente abaixo) quando o orçamento em si foi restaurado com
// sucesso, nunca em cima de uma falha.
async function restaurarOrcamentoNaoGravadoNoServidor(identificadorOrcamento: string, orcamentoOrfao: OrcamentoEmAndamento): Promise<boolean> {
    const produtos = orcamentoOrfao.Itens.map(converterItemParaProdutoMinimo) as unknown as IProduto[]
    const dadosOrcamentoEmAndamento = JSON.stringify({
        Orcamento: {
            ...orcamentoOrfao,
            TurnoEntrega: normalizarTurnoEntregaParaRestauracao(orcamentoOrfao.TurnoEntrega, orcamentoOrfao.DescricaoTurnoEntrega),
            Itens: orcamentoOrfao.Itens.map(item => ({
                ...item,
                TurnoEntrega: normalizarTurnoEntregaParaRestauracao(item.TurnoEntrega, item.DescricaoTurnoEntrega),
            })),
        }
    })
    const orcamentoRestaurado = await microuniversoApi.Orcamento.restaurarOrcamentoNaoGravado(identificadorOrcamento, produtos, dadosOrcamentoEmAndamento)

    if (orcamentoRestaurado == null) {

        console.error('Falha ao restaurar orçamento não gravado:', orcamentoRestaurado)
        //492886-return false
    }

    // 482572 — só sobrescreve o orçamento no Redux quando veio conteúdo de verdade. O `return false`
    // acima segue desativado (decisão da 492886: mesmo sem corpo na resposta, o atualizarOrcamento()
    // logo abaixo busca o estado consolidado do servidor), mas despachar `null` aqui zerava o
    // orçamento em memória antes dessa busca — pior que não despachar nada.
    if (orcamentoRestaurado != null) {
        store.dispatch(sobrescreverInformacoesAction(orcamentoRestaurado as IOrcamento))
    }

    // 482572 — o POST acima devolve o OrcamentoContract logo após incluir os itens e reaplicar os
    // campos pendentes; atualizarOrcamento() busca a versão consolidada (mesma chamada usada pelo
    // resto da tela, obterItensTotaisOrcamento — totais recalculados, itens com todos os dados) e
    // aplica via orcamentoController.model.atualizar (mesmo padrão de ProdutoLoteContainer.tsx) —
    // garante que a interface fique com TODOS os dados atualizados, não só o subconjunto do POST.

    await atualizarOrcamento()

    // Garante que a tela fique na aba de Itens (mesmo padrão do onAplicar comentado em
    // menuConsultaProdutosController.tsx): a chamada ao servidor acima é assíncrona — se o usuário
    // tiver navegado para outra aba enquanto ela estava em andamento, os itens recuperados
    // ficariam escondidos numa tela diferente sem isso.

    window.orcamentoController?.renderizarViewItemVenda()

    globalMU.showWarnMessage("Orçamento recuperado.<br>Nem todos os dados são recuperados automaticamente — confira os dados do orçamento antes de continuar.")

    return true
}

// 482572 — a mesma aba morta que deixou o orçamento não gravado pendente pode também ter deixado
// uma cesta de produtos pendente (mesmo abaId, ver PREFIXO_CHAVE_CESTA_LOCALSTORAGE em
// useRecuperarCestaProdutosAnterior.ts). Diferente da cesta de OUTRA aba morta (que ainda pede
// confirmação própria em useRecuperarCestaProdutosAnterior.ts), esta já foi implicitamente aceita
// junto com a confirmação do orçamento acima — por isso é recuperada aqui sem perguntar de novo.
// NÃO abre o modal da cesta (diferente de useRecuperarCestaProdutosAnterior.ts): aqui a tela já vai
// ficar na aba de Itens mostrando o orçamento recém-recuperado (ver renderizarViewItemVenda em
// restaurarOrcamentoNaoGravadoNoServidor) — abrir o modal por cima disso competiria com essa tela.
// Os produtos ficam só selecionados; o modal abre normalmente quando o usuário clicar no botão
// flutuante da cesta.
function recuperarCestaDaMesmaAbaAutomaticamente(abaId: string | null) {
    if (!abaId) return

    const chaveCesta = PREFIXO_CHAVE_CESTA_LOCALSTORAGE + abaId
    const cesta = lerCestaDeChaveLocalStorage(chaveCesta)
    if (!cesta || cesta.produtos.length === 0) return

    store.dispatch(selecionarProdutosAction(cesta.produtos))
    localStorage.removeItem(chaveCesta)
}

// 482572 — mesma checagem do cenário 2 do hook abaixo, extraída como função livre (sem
// useEffect/useState) para poder ser chamada de fora de React: o hook só roda quando
// ItensOrcamentoContainer ou PaginaPesquisaProdutoContainer chegam a montar, mas a aba que abre
// de fato depende do menu inicial favorito do usuário (estrela em orcamentoController.tsx —
// ativarMenuInicialFavorito/itemMenuSelecionado) e pode ser qualquer outra (Cliente, Forma de
// Pagamento, Markup...). orcamentoController.tsx::iniciar() chama esta função diretamente, antes
// de decidir a aba inicial, para o aviso de recuperação aparecer independente de qual aba abrir.
// O guard de módulo (jaVerificouEsteOrcamentoNaoGravado) evita pergunta duplicada: não importa se
// esta função e o hook (nos dois lugares que ainda o chamam) rodam para o mesmo orçamento — só o
// primeiro a chegar de fato verifica.
export async function verificarRecuperarOrcamentoNaoGravado(orcamento: IOrcamento | null): Promise<void> {
    if (orcamento?.TipoOrcamento == null) return
    // Orçamento somente leitura não pode receber os itens restaurados (nem qualquer outra edição) —
    // oferecer a recuperação aqui só levaria a uma tentativa de gravação que o servidor rejeitaria.
    if (orcamento.SomenteLeitura === true) return
    if (TIPOS_ORCAMENTO_COM_RECUPERACAO_NAO_GRAVADO.indexOf(orcamento.TipoOrcamento) === -1) return
    if (orcamento.Numero !== 0) return
    if (!orcamento.IdentificadorOrcamento) return
    if (!orcamento.CodigoFilialOrcamento) return
    if (jaVerificouEsteOrcamentoNaoGravado(orcamento.IdentificadorOrcamento)) return

    const identificadorOrcamento = orcamento.IdentificadorOrcamento
    const tipoOrcamento = orcamento.TipoOrcamento
    const codigoFilialOrcamento = orcamento.CodigoFilialOrcamento

    marcarOrcamentoNaoGravadoComoVerificado(identificadorOrcamento)

    const orfaos = await localizarOrcamentoEmAndamentoOrfaoDeOutraAba()
    // 482572 — só oferece um órfão do MESMO tipo e da MESMA filial do orçamento aberto agora
    // (CodigoFilialOrcamento/TipoOrcamento não mudam durante a edição de um orçamento ainda
    // não gravado — ver comentário em OrcamentoEmAndamento) — evita oferecer, por exemplo,
    // itens de uma Venda da filial 002 enquanto o usuário está numa Venda da filial 010.
    const orfaoNaoGravado = orfaos.find(o =>
        o.orcamento.Numero === 0 &&
        o.orcamento.TipoOrcamento === tipoOrcamento &&
        o.orcamento.CodigoFilialOrcamento === codigoFilialOrcamento &&
        (o.orcamento.Itens?.length ?? 0) > 0
    )
    if (!orfaoNaoGravado) return

    const quantidadeItens = orfaoNaoGravado.orcamento.Itens.length
    const descricaoTipo = DESCRICAO_TIPO_ORCAMENTO[orfaoNaoGravado.orcamento.TipoOrcamento as number] ?? "orçamento"
    const mensagem = `Existe um orçamento de ${descricaoTipo} com ${quantidadeItens} ${quantidadeItens === 1 ? "item" : "itens"} que ainda não foi gravado.\n\nDeseja recuperá-lo?`

    // 482572 — TRAVA ENTRE ABAS (Web Locks, ver travaEntreAbas.ts). O guard de módulo
    // `jaVerificouEsteOrcamentoNaoGravado` é por ABA e não protege deste cenário: duas abas abrindo
    // um orçamento novo ao mesmo tempo varrem o MESMO localStorage, acham o MESMO órfão e restauram
    // as duas — os itens do órfão entrariam em DOIS orçamentos diferentes. A janela era enorme,
    // porque a remoção do snapshot só acontecia no fim de tudo: o `window.confirm` abaixo pode ficar
    // aberto por minutos, e a restauração ainda faz uma ida demorada ao servidor.
    // `ifAvailable` = a segunda aba desiste na hora, sem fila e sem repetir a pergunta ao usuário.
    await executarComTravaExclusiva(`mu_restaurar_orcamento_${orfaoNaoGravado.chave}`, async () => {
        // Re-checa DENTRO da trava: entre a varredura e a aquisição, outra aba pode ter concluído a
        // restauração e removido o snapshot (double-checked locking) — sem isto, esta aba ainda
        // perguntaria ao usuário sobre um órfão que já não existe.
        if (!localStorage.getItem(orfaoNaoGravado.chave)) return

        if (!window.confirm(mensagem)) {
            // Recusa explícita: remove, para não perguntar de novo a cada orçamento novo (mesma
            // decisão já documentada em useRecuperarCestaProdutosAnterior.ts).
            removerOrcamentoEmAndamentoOrfao(orfaoNaoGravado.chave)
            return
        }

        try {
            globalMU.blockScreen({ message: "A restauração pode levar alguns instantes." });

            const restaurado = await restaurarOrcamentoNaoGravadoNoServidor(identificadorOrcamento, orfaoNaoGravado.orcamento)

            if (restaurado) {
                recuperarCestaDaMesmaAbaAutomaticamente(orfaoNaoGravado.abaId)
                // 482572 — o snapshot só é descartado depois de a restauração ter dado certo. Antes,
                // este `remover` ficava FORA do try/catch e rodava em qualquer desfecho: se a
                // restauração falhasse (exceção de rede/servidor), o usuário perdia o orçamento
                // inteiro e ainda por cima em silêncio (a mensagem de erro estava comentada).
                removerOrcamentoEmAndamentoOrfao(orfaoNaoGravado.chave)
            }
        } catch (error) {

            console.error('Erro ao restaurar orçamento não gravado:', error)
            // Snapshot preservado de propósito: o usuário pode tentar de novo no próximo orçamento
            // novo, em vez de perder os itens por causa de uma falha momentânea.
            globalMU.showErrorMessage("Falha ao restaurar o orçamento.<br>Os dados foram mantidos e a recuperação será oferecida novamente.")
        }
        finally {
            globalMU.unblockScreen()
        }
    })
}

// 491123 — recupera o snapshot do orçamento em andamento (ver orcamentoEmAndamentoSlice.ts) em
// dois cenários distintos. Em AMBOS, só oferece recuperação quando o snapshot tem itens de
// verdade (`Itens` não vazio) — CodigoCliente/CodigoFormaPagamento sozinhos não bastam, porque
// praticamente todo orçamento ganha um cliente/forma de pagamento padrão assim que é criado
// (ex: Consumidor Final, CodigoCliente=0), o que faria o aviso disparar mesmo sem nada de fato
// para recuperar:
//
// 1) Orçamento JÁ GRAVADO reaberto (Numero do orçamento atual bate com o Numero salvo): os itens
//    já vêm completos do servidor — só reaplica os campos que podem ter sido editados localmente
//    e ainda não persistidos (tipo de entrega, filial de retirada/transferência, etc.).
//
// 2) Orçamento atual ainda NÃO gravado de verdade (Numero == 0, ou == 7 — sentinela de pedido
//    importado de e-commerce, ver foiGravado) e o snapshot salvo também não é de um orçamento já
//    gravado, mesmo tipo de orçamento: os itens do snapshot não existem no servidor — uma única
//    chamada ao servidor (restaurarOrcamentoNaoGravadoNoServidor, acima) inclui os itens e reaplica
//    os campos pendentes. Igual ao feito em useRecuperarCestaProdutosAnterior.ts para a cesta de
//    produtos, não exige que o orçamento atual esteja vazio — Numero == 7 já chega com itens do
//    e-commerce, e a inclusão é sempre em modo ANEXAR, nunca sobrescreve o que já existe.
export function useRecuperarOrcamentoEmAndamento(orcamento: IOrcamento | null) {
    // 491123 — o cenario "orcamento ja gravado reaberto" agora e restaurado no proprio servidor
    // (ver OrcamentoController.RestaurarOrcamento), entao so falta limpar aqui o snapshot local
    // que ficou obsoleto: _Menu.cshtml seta CHAVE_SESSION_RESTAURANDO_AUTOMATICAMENTE antes de
    // navegar para /Orcamento/RestaurarOrcamento; ao carregar a pagina com o orcamento restaurado,
    // descarta o snapshot para nao repetir o aviso de recuperacao num proximo login.
    useEffect(() => {
        if (!orcamento?.Numero) return
        if (sessionStorage.getItem(CHAVE_SESSION_RESTAURANDO_AUTOMATICAMENTE) !== 'true') return

        sessionStorage.removeItem(CHAVE_SESSION_RESTAURANDO_AUTOMATICAMENTE)
        store.dispatch(limparOrcamentoEmAndamentoAction())
        globalMU.showWarnMessage("Orçamento recuperado.<br>Nem todos os dados são recuperados automaticamente — confira os dados do orçamento antes de continuar.")
    }, [orcamento?.Numero])

    // 482572 — cenário 2 (orçamento NOVO ainda não gravado, ver comentário acima da função): Venda,
    // Outras Saídas e Transferência (ver TIPOS_ORCAMENTO_COM_RECUPERACAO_NAO_GRAVADO acima). Só
    // procura órfãos de OUTRAS abas (mesmo princípio de useRecuperarCestaProdutosAnterior.ts): o
    // snapshot da PRÓPRIA aba, para um orçamento novo, é o espelho em tempo real do PRÓPRIO
    // orçamento sendo criado agora (store.ts reescreve a cada mudança) — não existe "rascunho antigo
    // da própria aba" para comparar sem essa mesma escrita já ter sobrescrito a evidência.
    useEffect(() => {
        verificarRecuperarOrcamentoNaoGravado(orcamento)
    }, [orcamento?.TipoOrcamento, orcamento?.Numero, orcamento?.IdentificadorOrcamento, orcamento?.CodigoFilialOrcamento])
}
